import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../services/api';
import './AnalyticsDashboard.css';

interface TopPost {
  post_id: string;
  total_time_ms: number;
  view_count: number;
  content: string | null;
  author_name: string | null;
}

interface ActiveUsersPoint {
  date: string;
  count: number;
}

interface PageView {
  page: string;
  count: number;
}

interface Summary {
  top_posts_by_time: TopPost[];
  active_users: ActiveUsersPoint[];
  page_views: PageView[];
  total_events: number;
  unique_users: number;
  avg_session_time_ms: number;
}

const DAYS_OPTIONS = [7, 14, 30, 90];

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const AnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    setError(null);
    analyticsAPI.getDashboard(days)
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <header className="page-header"><h2>Analytics</h2></header>
        <div className="analytics-loading">Loading analytics...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="analytics-dashboard">
        <header className="page-header"><h2>Analytics</h2></header>
        <div className="analytics-error">{error || 'No data'}</div>
      </div>
    );
  }

  const maxActiveUsers = Math.max(...(data.active_users?.map(p => p.count) || [1]), 1);
  const maxPageViews = Math.max(...(data.page_views?.map(p => p.count) || [1]), 1);

  return (
    <div className="analytics-dashboard">
      <header className="page-header">
        <h2>Analytics</h2>
        <div className="days-selector">
          {DAYS_OPTIONS.map((d) => (
            <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </header>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{data.total_events.toLocaleString()}</div>
          <div className="stat-label">Total Events</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{data.unique_users.toLocaleString()}</div>
          <div className="stat-label">Unique Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatDuration(data.avg_session_time_ms)}</div>
          <div className="stat-label">Avg Page Time</div>
        </div>
      </div>

      {data.active_users && data.active_users.length > 0 && (
        <div className="analytics-section">
          <h3>Daily Active Users</h3>
          <div className="bar-chart">
            {data.active_users.slice(-14).map((point) => (
              <div key={point.date} className="bar-row">
                <span className="bar-label">{formatDate(point.date)}</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${(point.count / maxActiveUsers) * 100}%` }} />
                </div>
                <span className="bar-value">{point.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.page_views && data.page_views.length > 0 && (
        <div className="analytics-section">
          <h3>Top Pages</h3>
          <div className="bar-chart">
            {data.page_views.map((pv) => (
              <div key={pv.page} className="bar-row">
                <span className="bar-label" title={pv.page}>{pv.page}</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${(pv.count / maxPageViews) * 100}%` }} />
                </div>
                <span className="bar-value">{pv.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.top_posts_by_time && data.top_posts_by_time.length > 0 && (
        <div className="analytics-section">
          <h3>Top Posts by Time Spent</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Author</th>
                <th>Views</th>
                <th>Total Time</th>
              </tr>
            </thead>
            <tbody>
              {data.top_posts_by_time.map((post) => (
                <tr key={post.post_id}>
                  <td>{post.content ? (post.content.length > 50 ? post.content.slice(0, 50) + '...' : post.content) : '(media post)'}</td>
                  <td>{post.author_name || 'Unknown'}</td>
                  <td>{post.view_count}</td>
                  <td>{formatDuration(post.total_time_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
